import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import './LegalPage.css';

type LegalDoc = 'privacy' | 'terms';
type SupportedLang = 'ko' | 'en';

interface FetchedDoc {
  doc: LegalDoc;
  lang: SupportedLang;
  status: 'loaded' | 'error';
  content: string | null;
}

function resolveLang(raw: string | undefined): SupportedLang {
  return raw === 'en' ? 'en' : 'ko';
}

function stripFrontmatter(source: string): string {
  return source.replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n+/, '');
}

export function LegalPage({ doc }: { doc: LegalDoc }) {
  const { t } = useTranslation('common');
  const { lang: langParam } = useParams<{ lang?: string }>();
  const lang = resolveLang(langParam);
  const [fetched, setFetched] = useState<FetchedDoc | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/legal/${doc}-${lang}.md`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (cancelled) return;
        setFetched({ doc, lang, status: 'loaded', content: stripFrontmatter(text) });
      })
      .catch(() => {
        if (cancelled) return;
        setFetched({ doc, lang, status: 'error', content: null });
      });
    return () => {
      cancelled = true;
    };
  }, [doc, lang]);

  // Only use fetched data if it matches the current doc/lang (avoids flashing stale content)
  const current = fetched?.doc === doc && fetched.lang === lang ? fetched : null;

  return (
    <main className="legal-page">
      <article className="legal-page-content">
        {current === null && <p className="legal-page-status">{t('legal.loading')}</p>}
        {current?.status === 'error' && (
          <p className="legal-page-status" role="alert">
            {t('legal.loadFailed')}
          </p>
        )}
        {current?.status === 'loaded' && current.content !== null && (
          <ReactMarkdown>{current.content}</ReactMarkdown>
        )}
      </article>
    </main>
  );
}
